import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from "@sportscanner/ui";
import { toLocalDateStr, addDays } from "./helpers";
import DatePicker from "./date-picker";

// ISO weekday: Mon=1..Sun=7.
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type Frequency = "DAILY" | "WEEKLY";

export interface RecurrenceValue {
  frequency: Frequency;
  weekdays: Weekday[];
  untilDate: string; // YYYY-MM-DD
}

const WEEKDAY_KEYS: Record<Weekday, string> = {
  1: "mon",
  2: "tue",
  3: "wed",
  4: "thu",
  5: "fri",
  6: "sat",
  7: "sun",
};

export function weekdayOf(dateStr: string): Weekday {
  const d = new Date(dateStr + "T12:00:00");
  const js = d.getDay(); // Sun=0..Sat=6
  return (js === 0 ? 7 : js) as Weekday;
}

export function defaultRecurrence(startDate: string): RecurrenceValue {
  // Default: weekly on the same weekday for 4 weeks.
  return {
    frequency: "WEEKLY",
    weekdays: startDate ? [weekdayOf(startDate)] : [1],
    untilDate: startDate
      ? toLocalDateStr(addDays(new Date(startDate + "T12:00:00"), 28))
      : toLocalDateStr(addDays(new Date(), 28)),
  };
}

interface Props {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  value: RecurrenceValue;
  onChange: (v: RecurrenceValue) => void;
  /** First occurrence — drives weekday defaults and the until-date minimum. */
  startDate: string;
  disabled?: boolean;
}

export default function RecurrenceField({
  enabled,
  onEnabledChange,
  value,
  onChange,
  startDate,
  disabled,
}: Props) {
  const { t } = useTranslation();

  const startWeekday = useMemo<Weekday | null>(
    () => (startDate ? weekdayOf(startDate) : null),
    [startDate],
  );

  // Cap the until-date input at today + 90 days; clamp on every change in
  // case the user has been on the dialog past midnight.
  const maxUntilDate = useMemo(
    () => toLocalDateStr(addDays(new Date(), 90)),
    [],
  );
  const minUntilDate = startDate || toLocalDateStr(new Date());

  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-sm font-semibold text-neutral-700">
            {t("bookings.form.repeat")}
          </Label>
          <p className="text-xs text-neutral-500">
            {t("bookings.form.repeatHint")}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onEnabledChange}
          disabled={disabled}
          aria-label={t("bookings.form.repeat")}
        />
      </div>

      {enabled && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-neutral-600">
              {t("bookings.form.frequency")}
            </Label>
            <Select
              value={value.frequency}
              onValueChange={(v: Frequency) =>
                onChange({ ...value, frequency: v })
              }
              disabled={disabled}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DAILY">
                  {t("bookings.form.frequencyDaily")}
                </SelectItem>
                <SelectItem value="WEEKLY">
                  {t("bookings.form.frequencyWeekly")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {value.frequency === "WEEKLY" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-neutral-600">
                {t("bookings.form.weekdays")}
              </Label>
              <ToggleGroup
                type="multiple"
                value={value.weekdays.map(String)}
                onValueChange={(vals) => {
                  if (vals.length === 0 && startWeekday) {
                    onChange({ ...value, weekdays: [startWeekday] });
                    return;
                  }
                  const next = vals
                    .map((s) => Number(s) as Weekday)
                    .sort((a, b) => a - b);
                  onChange({ ...value, weekdays: next });
                }}
                className="flex flex-wrap gap-1.5"
                disabled={disabled}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                  const wd = d as Weekday;
                  return (
                    <ToggleGroupItem
                      key={d}
                      value={String(d)}
                      className="w-9 h-9 rounded-full border border-neutral-200 text-xs font-semibold cursor-pointer data-[state=off]:bg-white data-[state=off]:text-neutral-600 data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:border-primary-500"
                    >
                      {t(`days.short.${WEEKDAY_KEYS[wd]}`)}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-neutral-600">
              {t("bookings.form.repeatUntil")}
            </Label>
            <DatePicker
              value={value.untilDate}
              onChange={(next) => onChange({ ...value, untilDate: next })}
              min={minUntilDate}
              max={maxUntilDate}
              disabled={disabled}
            />
            <p className="text-[11px] text-neutral-400">
              {t("bookings.form.repeatCapHint")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
